import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Container, ListGroup,
} from 'react-bootstrap';
import TransactionConfirmationModal from './TransactionConfirmationModal';
import store from '../../../store';
import TransactionService from '../../../service/TransactionService';
import { buildFailureStatus, buildSuccessStatus, } from '../../constants/AlertConstants';

class MixedResultView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTransactions: {},
      selectedLineItems: {},
      countOfV3: 0,
      countOfV2: 0,
      showModal: false,
      changeSoidRequestResult: null,
      requestWasSubmitted: false,
    };
  }

  componentDidMount() {
    if (this.props.transactionData.length !== 0) {
      const countOfV3 = this.props.transactionData.reduce((count, transaction) => {
        return transaction.transactionType === 'V3' ? count + 1 : count;
      }, 0);

      const countOfV2 = this.props.transactionData.reduce((count, transaction) => {
        return transaction.transactionType === 'V2' ? count + 1 : count;
      }, 0);

      this.setState({ countOfV3, countOfV2, });
    }
  }

  handleSubmit = () => {
    this.setState({ showModal: true, });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, });
  };

  onDismiss() {
    return () => this.setState({
      changeSoidRequestResult: null,
    });
  }

  constructV2Payloads = async (v2Transactions, basePayloadFormat) => {
     
    return await Promise.all(
      v2Transactions.map(async (transactionId) => {
        const response = await TransactionService.getChangeSoidHistory(transactionId);
        const changeSoidHistory = response.data.data;

        // ensure sort order is consistent, last item in array is the most recent change
        changeSoidHistory.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        const mostRecentChangeSoidHistory = changeSoidHistory[changeSoidHistory.length - 1];

        return {
          ...basePayloadFormat,
          transactionIdLineItemIdPairs: { [transactionId]: [ '', ], },
          newSoid: mostRecentChangeSoidHistory.originalSoid,
        };
      })
    );
  };

  returnMostRecentRelevantV3ChangeSoidHistory = (transactionId, lineItemId, changeSoidHistory) => {
    let mostRecentChangeSoidHistory = null;

    // ensure sort order is consistent, last item in array is the most recent change
    changeSoidHistory.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    for (let i = changeSoidHistory.length - 1; i >= 0; i--) {
      // get the last item in response.data array && make sure it has the same lineItemId
      if (changeSoidHistory[i].lineItemId === lineItemId && changeSoidHistory[i].transactionId === transactionId) {
        mostRecentChangeSoidHistory = changeSoidHistory[i];
        break;
      }
    }

    return mostRecentChangeSoidHistory;
  };

  constructV3Payloads = async (v3Transactions, basePayloadFormat) => {
     
    return await Promise.all(
      v3Transactions.map(async (v3TransactionObject) => {
        const response = await TransactionService.getChangeSoidHistory(v3TransactionObject.transactionId);
        const mostRecentChangeSoidHistory = this.returnMostRecentRelevantV3ChangeSoidHistory(
          v3TransactionObject.transactionId,
          v3TransactionObject.lineItemId,
          response.data.data
        );
        return {
          ...basePayloadFormat,
          transactionIdLineItemIdPairs: { [v3TransactionObject.transactionId]: [ v3TransactionObject.lineItemId, ], },
          newSoid: mostRecentChangeSoidHistory.originalSoid,
        };
      })
    );
  };

  prepareV2Transactions = (selectedTransactions) => {
    return Object.entries(selectedTransactions)
      .filter(([ , transactionData, ]) => transactionData.transactionType === 'V2')
      .map(([ transactionId, ]) => transactionId);
  };

  prepareV3Transactions = (selectedLineItems) => {
    return Object.entries(selectedLineItems)
      .flatMap(([ transactionId, lineItems, ]) => Object.keys(lineItems).map(lineItemId => ({ transactionId, lineItemId, })));
  };

  constructAndSendPayloads = async (reasonDropDownValue, textValue, selectedTransactions, selectedLineItems) => {
    const v2Transactions = this.prepareV2Transactions(selectedTransactions);
    const v3Transactions = this.prepareV3Transactions(selectedLineItems);

    const basePayloadFormat = {
      normalizedReason: reasonDropDownValue,
      userReason: textValue,
      userId: store.getState().auth.authentication.tenantUserId,
    };

    try {
      const [ v2Payloads, v3Payloads, ] = await Promise.all([
        this.constructV2Payloads(v2Transactions, basePayloadFormat),
        this.constructV3Payloads(v3Transactions, basePayloadFormat),
      ]);

      const payloads = [ ...v2Payloads, ...v3Payloads, ];

      await Promise.all(payloads.map(payload => TransactionService.changeSoid(payload)));

      this.setState({
        changeSoidRequestResult: buildSuccessStatus('Your transactions were successfully reverted to their original SOID.', this.onDismiss()),
        requestWasSubmitted: true,
      });
    } catch (error) {
      this.setState({
        changeSoidRequestResult: buildFailureStatus('Your transactions were not successfully reverted to their original SOID.', this.onDismiss()),
        requestWasSubmitted: true,
      });
    }
  };

  submitGenResolveChangeSoidRequest = (reasonDropDownValue, textValue) => {
    const preparedTransactionIds = this.prepareTransactionIds(this.state.selectedTransactions, this.state.selectedLineItems);
    const changeSoidPayload = {
      transactionIdLineItemIdPairs: preparedTransactionIds,
      normalizedReason: reasonDropDownValue,
      userReason: textValue,
      newSoid: 'gen_resolve',
      userId: store.getState().auth.authentication.tenantUserId,
    };

    TransactionService.changeSoid(changeSoidPayload).then((() => {
      this.setState({
        changeSoidRequestResult: buildSuccessStatus('Your gen_resolve request was successful.', this.onDismiss()),
        requestWasSubmitted: true,
      });
    })).catch(() => {
      this.setState({
        changeSoidRequestResult: buildFailureStatus('Your gen_resolve request was unsuccessful.', this.onDismiss()),
        requestWasSubmitted: true,
      });
    });
  };

  pluckV2Soid = (transactionId, transactionData) => {
    return transactionData.serviceOfferingId;
  };

  pluckV3Soid = (transactionId, lineItemId, transactionData) => {
    return transactionData.lineItems.reduce((soid, lineItemObject) => {
      if (lineItemObject._id === lineItemId) {
        return lineItemObject.serviceOfferingId;
      }
      return soid;
    }, null);
  };

  pruneOriginalSoidTransactions = (selectedTransactions, selectedLineItems) => {
    const prunedSelectedTransactions = { ...selectedTransactions, };
    const prunedSelectedLineItems = { ...selectedLineItems, };

    Object.entries(selectedTransactions).forEach(([ transactionId, transactionData, ]) => {
      if (transactionData.transactionType === 'V2') {
        const soid = this.pluckV2Soid(transactionId, transactionData.data.transaction);
        if (soid !== 'gen_resolve') {
          delete prunedSelectedTransactions[transactionId];
        }
      } else {
        const lineItems = selectedLineItems[transactionId];
        Object.keys(lineItems).forEach(lineItemId => {
          const soid = this.pluckV3Soid(transactionId, lineItemId, transactionData.data.v3transaction);
          if (soid !== 'gen_resolve') {
            delete prunedSelectedLineItems[transactionId][lineItemId];
            // remove transaction if it is associated with no line items
            if (Object.keys(prunedSelectedLineItems[transactionId]).length === 0) {
              delete prunedSelectedTransactions[transactionId];
              delete prunedSelectedLineItems[transactionId];
            }
          }
        });
      }
    });

    return [ prunedSelectedTransactions, prunedSelectedLineItems, ];
  };

  handleModalSubmit = async (soidShouldBeGenResolve, reasonDropDownValue, textValue) => {
    this.setState({ showModal: false, });

    const trimmedTextValue = textValue.trim();

    if (soidShouldBeGenResolve) {
      this.submitGenResolveChangeSoidRequest(reasonDropDownValue, trimmedTextValue);
      return;
    }

    // remove transactions that are not gen_resolve, we don't want to revert them
    const [ prunedSelectedTransactions, prunedSelectedLineItems, ] = this.pruneOriginalSoidTransactions(
      this.state.selectedTransactions,
      this.state.selectedLineItems
    );

    await this.constructAndSendPayloads(reasonDropDownValue, trimmedTextValue, prunedSelectedTransactions, prunedSelectedLineItems);
  };

  // format transactions for the changeSoid endpoint
  prepareTransactionIds = (selectedTransactions, selectedLineItems) => {
    const finalizedTransactionIds = {};

    Object.entries(selectedTransactions).forEach(([ transactionId, transactionData, ]) => {
      if (transactionData.transactionType === 'V2') {
        finalizedTransactionIds[transactionId] = [ '', ];
      }
    });

    Object.entries(selectedLineItems).forEach(([ transactionId, lineItems, ]) => {
      finalizedTransactionIds[transactionId] = Object.keys(lineItems);
    });

    return finalizedTransactionIds;
  };

  handleLineItemCheckboxChange = (e, transactionId, lineItemId) => {
    const { transactionData, } = this.props;

    if (e.target.checked) {
      const parentTransactionData = transactionData.find(
        transaction => transaction.data.v3transaction
        && transaction.data.v3transaction.id === transactionId
      );

      // after a check event, update both selectedTransactions(v2 & v3) and selectedLineItems(v3)
      this.setState(prevState => ({
        selectedTransactions: {
          ...prevState.selectedTransactions,
          [transactionId]: parentTransactionData,
        },
        selectedLineItems: {
          ...prevState.selectedLineItems,
          [transactionId]: {
            ...prevState.selectedLineItems[transactionId],
            [lineItemId]: true,
          },
        },
      }));
    } else { // if not checked (or unchecked), remove the line item from the relevant state(s)
      this.setState(prevState => {
        const newSelectedLineItems = { ...prevState.selectedLineItems, };
        const newSelectedTransactions = { ...prevState.selectedTransactions, };
        if (newSelectedLineItems[transactionId]) {
          delete newSelectedLineItems[transactionId][lineItemId];
          if (Object.keys(newSelectedLineItems[transactionId]).length === 0) {
            delete newSelectedLineItems[transactionId];
            // remove parent transaction ID from selectedTransactions, too
            delete newSelectedTransactions[transactionId];
          }
        }
        return { selectedLineItems: newSelectedLineItems, selectedTransactions: newSelectedTransactions, };
      });
    }
  };

  handleCheckboxChange = (e, transaction) => {
    const { selectedTransactions, selectedLineItems, } = this.state;

    const transactionId = transaction.transactionType === 'V3' ? transaction.data.v3transaction.id : transaction.data.transaction.id;
    if (e.target.checked) {
      const newSelectedLineItems = { ...selectedLineItems, };
      if (transaction.transactionType === 'V3') {
        newSelectedLineItems[transactionId] = transaction.data.v3transaction.lineItems.reduce((acc, lineItem) => {
          acc[lineItem._id] = true;
          return acc;
        }, {});
      }
      this.setState({
        selectedTransactions: {
          ...selectedTransactions,
          [transactionId]: transaction,
        },
        selectedLineItems: newSelectedLineItems,
      });
    } else {
      const newSelectedTransactions = { ...selectedTransactions, };
      const newSelectedLineItems = { ...selectedLineItems, };
      delete newSelectedTransactions[transactionId];
      if (transaction.transactionType === 'V3') {
        delete newSelectedLineItems[transactionId];
      }
      this.setState({ selectedTransactions: newSelectedTransactions, selectedLineItems: newSelectedLineItems, });
    }
  };

  handleSelectAll = () => {
    const { transactionData, } = this.props;
    const newSelectedTransactions = {};
    const newSelectedLineItems = {};

    transactionData.forEach(transaction => {
      const transactionId = transaction.transactionType === 'V3' ? transaction.data.v3transaction.id : transaction.data.transaction.id;
      newSelectedTransactions[transactionId] = transaction;
      if (transaction.transactionType === 'V3') {
        const lineItems = transaction.data.v3transaction.lineItems;
        lineItems.forEach(lineItem => {
          if (!newSelectedLineItems[transactionId]) {
            newSelectedLineItems[transactionId] = {};
          }
          newSelectedLineItems[transactionId][lineItem._id] = true;
        });
      }
    });

    this.setState({
      selectedTransactions: newSelectedTransactions,
      selectedLineItems: newSelectedLineItems,
    });
  };

  removeAll = () => {
    this.setState({ selectedTransactions: {}, selectedLineItems: {}, });
  };

  renderV2Transactions() {
    return (
      <div style={{
        backgroundColor: '#f8f9fa', borderRadius: '5px', padding: '20px', marginBottom: '20px',
      }}
      >
        <h4 style={{ color: '#333', marginBottom: '20px', }}>V2 Transaction Data</h4>
        <ListGroup variant="flush">
          {this.props.transactionData.map((transaction) => (
            transaction.transactionType === 'V2'
              ? (
                <ListGroup.Item
                  key={transaction.transactionId}
                  style={{
                    border: 'none', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)', marginBottom: '10px', borderRadius: '5px',
                  }}
                >
                  <div key={transaction.transactionId}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', }}>
                      <div style={{ display: 'flex', alignItems: 'center', }}>
                        <input
                          type="checkbox"
                          checked={!!this.state.selectedTransactions[transaction.data.transaction.id]}
                          onChange={e => this.handleCheckboxChange(e, transaction)}
                        />
                        <span style={{ marginRight: '5px', }} />Transaction ID: {transaction.data.transaction.id}
                      </div>
                      <span>{transaction.data.transaction.transactionType}</span>
                    </div>
                    <ul>
                      <li>Charged Amount: {transaction.data.transaction.chargedAmount}</li>
                      <li>Service Offering ID: {transaction.data.transaction.serviceOfferingId}</li>
                    </ul>
                  </div>
                </ListGroup.Item>
              )
              : null
          ))}
        </ListGroup>
        {this.state.countOfV2 === 0 ? (
          <h6 style={{ color: '#999', marginTop: '20px', }}>No V2 Transactions found.</h6>
        ) : null}
      </div>
    );
  }

  renderV3Transactions() {
    return (
      <div style={{
        backgroundColor: '#f8f9fa', borderRadius: '5px', padding: '20px', marginBottom: '20px',
      }}
      >
        <h4 style={{ color: '#333', marginBottom: '20px', }}>V3 Transaction Data</h4>
        <ListGroup variant="flush">
          {this.props.transactionData.map((transaction) => (
            transaction.transactionType === 'V3'
              ? (
                <ListGroup.Item
                  key={transaction.data.v3transaction.id}
                  style={{
                    border: 'none', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)', marginBottom: '10px', borderRadius: '5px',
                  }}
                >
                  <div key={transaction.data.v3transaction.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', }}>
                      <div style={{ display: 'flex', alignItems: 'center', }}>
                        <input
                          type="checkbox"
                          checked={!!this.state.selectedTransactions[transaction.data.v3transaction.id]}
                          onChange={e => this.handleCheckboxChange(e, transaction)}
                          style={{ marginRight: '5px', }}
                        />
                        <span> Transaction ID: {transaction.data.v3transaction.id}</span>
                      </div>
                      <span>{transaction.data.v3transaction.transactionType}</span>
                    </div>
                    {transaction.data.v3transaction.lineItems.map((lineItem) => (
                      <div key={`${transaction.data.v3transaction.id}-${lineItem._id}`} style={{ marginLeft: '20px', }}>
                        <input
                          type="checkbox"
                          checked={
                            !!(
                              this.state.selectedLineItems[transaction.data.v3transaction.id]
                              && this.state.selectedLineItems[transaction.data.v3transaction.id][lineItem._id]
                            )
                          }
                          onChange={e => this.handleLineItemCheckboxChange(e, transaction.data.v3transaction.id, lineItem._id)}
                        />
                        <span style={{ marginRight: '5px', }} />Line Item ID: {lineItem._id}
                        <ul>
                          <li>Total Amount: {lineItem.amount}</li>
                          <li>Service Offering ID: {lineItem.serviceOfferingId}</li>
                        </ul>
                      </div>
                    ))}
                  </div>
                </ListGroup.Item>
              )
              : null
          ))}
        </ListGroup>
        {this.state.countOfV3 === 0 ? (
          <h6 style={{ color: '#999', marginTop: '20px', }}>No V3 Transactions found.</h6>
        ) : null}
      </div>
    );
  }

  renderSomeTransactionsNotFound() {
    return (
      <>
        {this.props.notFoundTransactions.length > 0 ? (
          <div style={{
            backgroundColor: '#f8f9fa', borderRadius: '5px', padding: '20px', marginBottom: '20px',
          }}
          >
            <h4 style={{ color: '#333', marginBottom: '20px', }}>Unknown Transaction IDs</h4>
            <p style={{ color: '#666', marginBottom: '20px', }}>The following transactions were not found in our database:</p>
            <ListGroup variant="flush">
              {this.props.notFoundTransactions.map((transaction) => (
                <ListGroup.Item
                  key={transaction}
                  style={{
                    border: 'none', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)', marginBottom: '10px', borderRadius: '5px',
                  }}
                >
                  Transaction ID: {transaction}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        ) : null}
      </>
    );
  }

  renderButtons() {
    const { selectedTransactions, selectedLineItems, } = this.state;
    const noSelections = Object.keys(selectedTransactions).length === 0 && Object.keys(selectedLineItems).length === 0;

    return (
      <>
        <Button onClick={this.handleSelectAll} style={{ marginRight: '5px', }}>Select All</Button>
        <Button onClick={this.removeAll} variant="danger">Remove All Selections</Button>
        <br />
        <br />
        {this.state.changeSoidRequestResult}
        {noSelections ? (<Alert variant="warning">No Transactions Selected.</Alert>) : null}
        <Button onClick={this.handleSubmit} disabled={noSelections || this.state.requestWasSubmitted}>Review Selected Transactions</Button>
        <br />
        <br />
        <br />
      </>
    );
  }

  renderConfirmationModal() {
    return (
      <>
        <TransactionConfirmationModal
          isOpen={this.state.showModal}
          closeModal={this.handleCloseModal}
          handleSubmit={this.handleModalSubmit}
          transactions={this.state.selectedTransactions}
          selectedLineItems={this.state.selectedLineItems}
        />
      </>
    );
  }

  render() {
    return (
      <Container>
        {this.renderSomeTransactionsNotFound()}
        {this.renderV3Transactions()}
        {this.renderV2Transactions()}
        <br />
        {this.renderButtons()}
        {this.renderConfirmationModal()}
      </Container>
    );
  }
}

MixedResultView.propTypes = {
  transactionData: PropTypes.array.isRequired,
  notFoundTransactions: PropTypes.array.isRequired,
};

export default MixedResultView;
